import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Minus, Plus, ArrowRight } from "lucide-react";
import { formatAttribution } from "@/lib/text";

const SIZES = [
  { key: "s", label: "S", base: "text-[16px]", h: "text-3xl" },
  { key: "m", label: "M", base: "text-[18px]", h: "text-4xl" },
  { key: "l", label: "L", base: "text-[20px]", h: "text-5xl" },
  { key: "xl", label: "XL", base: "text-[22px]", h: "text-6xl" },
];

export default function ReadingMode() {
  const { weekNumber } = useParams();
  const nav = useNavigate();
  const [week, setWeek] = useState(null);
  const [edition] = useState(() => localStorage.getItem("atc_edition") || "adult");
  const [size, setSize] = useState(() => localStorage.getItem("atc_read_size") || "m");
  const sz = SIZES.find((s) => s.key === size) || SIZES[1];

  useEffect(() => {
    api.get(`/curriculum/weeks/${weekNumber}`, { params: { edition } }).then((r) => setWeek(r.data));
    window.scrollTo(0, 0);
  }, [weekNumber, edition]);

  const setSizeKey = (k) => { setSize(k); localStorage.setItem("atc_read_size", k); };
  const prev = +weekNumber > 1 ? +weekNumber - 1 : null;
  const next = +weekNumber < 11 ? +weekNumber + 1 : null;

  if (!week) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen parchment">
      {/* Reading toolbar */}
      <div className="border-b border-border bg-background/85 backdrop-blur sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to={`/app/weeks/${weekNumber}`} data-testid="reading-exit"
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
            <ArrowLeft className="inline w-3 h-3 mr-1" /> Exit reading mode
          </Link>
          <div className="flex items-center gap-1 border border-border" role="group" aria-label="Text size">
            {SIZES.map((s) => (
              <button key={s.key} onClick={() => setSizeKey(s.key)}
                data-testid={`reading-size-${s.key}`}
                className={`px-2.5 py-1 text-xs uppercase tracking-widest ${size === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <article className={`max-w-2xl mx-auto px-6 py-16 font-serif ${sz.base}`} style={{ lineHeight: 1.75 }}>
        <p className="text-xs uppercase tracking-[0.25em] text-primary font-sans not-italic mb-3">
          Week {week.week_number} · {week.movement}{edition === "youth" ? " · Youth" : ""}
        </p>
        <h1 className={`${sz.h} leading-tight tracking-tight mb-10`}>{week.title}</h1>

        <div className="border-l-2 border-primary pl-6 my-10">
          <div className="text-xs uppercase tracking-widest text-primary font-sans not-italic mb-2">{week.opening_scripture_ref}</div>
          <p className="italic whitespace-pre-wrap">{week.opening_scripture_text}</p>
        </div>

        {week.intro.split("\n\n").map((p, i) => (
          <p key={i} className="mb-6">{p}</p>
        ))}

        <div className="border border-border bg-card p-8 my-14 shadow-[0_8px_30px_rgba(42,42,38,0.06)]">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-sans not-italic mb-3">Memory verse</div>
          <div className="text-xs text-primary mb-4 font-sans not-italic">{week.memory_verse_ref}</div>
          <blockquote className="italic">{week.memory_verse_text}</blockquote>
          <div className="mt-5 text-right italic text-sm text-muted-foreground tracking-wide font-sans not-italic">
            <span className="italic">{formatAttribution(week.memory_verse_ref)}</span>
          </div>
        </div>

        <div className="border-l-2 border-primary pl-6 my-10">
          <div className="text-xs uppercase tracking-widest text-primary font-sans not-italic mb-2">Closing prayer</div>
          <p className="italic">{week.closing_prayer}</p>
        </div>

        <div className="mt-16 pt-8 border-t border-border flex justify-between text-sm font-sans not-italic">
          {prev ? (
            <button onClick={() => nav(`/app/weeks/${prev}/read`)} data-testid="reading-prev"
              className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="inline w-4 h-4 mr-1" /> Week {prev}
            </button>
          ) : <span />}
          {next && (
            <button onClick={() => nav(`/app/weeks/${next}/read`)} data-testid="reading-next"
              className="text-primary underline-serif">
              Week {next} <ArrowRight className="inline w-4 h-4 ml-1" />
            </button>
          )}
        </div>
      </article>
    </div>
  );
}
